import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Spinner } from '@seddi/ui-kit';
import { useEffect } from 'react';
import styled from 'styled-components';
import { DecreaseIcon, IncreaseIcon } from '@/icons/ZoomIcons';
import { DEFAULT_CAMERA_FAR, DEFAULT_CAMERA_FOV, DEFAULT_CAMERA_NEAR } from '@/viewer/constants';
import { useAppViewer } from './AppViewer.hooks';
import { useColorwayPicker } from './useColorwayPicker';
import { formatRecipeExpiry } from './saveExit';
import { Viewer } from './Viewer';
const DEFAULT_CAMERA = {
    far: DEFAULT_CAMERA_FAR,
    fov: DEFAULT_CAMERA_FOV,
    near: DEFAULT_CAMERA_NEAR,
};
const Container = styled.div `
  background-color: rgba(255, 255, 255, 0);
  display: block;
  flex: 1;
  height: 100%;
  position: relative;
  min-width: 0;
`;
const PreviewImage = styled.img `
  display: block;
  height: 100%;
  object-fit: contain;
  width: 100%;
  filter: blur(5px);
`;
const LoadingSpinner = styled.div `
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;
const MessageOverlay = styled.div `
  align-items: center;
  color: #333;
  display: flex;
  font-size: 1rem;
  height: 100%;
  justify-content: center;
  padding: 1rem;
  text-align: center;
  width: 100%;
`;
const ColorwaySelect = styled.select `
  width: 140px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  ${({ theme }) => theme.font.t3};
  color: ${({ $controlsStyle, theme }) => { var _a; return (_a = $controlsStyle === null || $controlsStyle === void 0 ? void 0 : $controlsStyle.textColor) !== null && _a !== void 0 ? _a : theme.color.ui3; }};
  background-color: ${({ $controlsStyle, theme }) => { var _a; return (_a = $controlsStyle === null || $controlsStyle === void 0 ? void 0 : $controlsStyle.backgroundColor) !== null && _a !== void 0 ? _a : theme.color.ui6; }};
  border: 1px solid
    ${({ $controlsStyle, theme }) => { var _a; return (_a = $controlsStyle === null || $controlsStyle === void 0 ? void 0 : $controlsStyle.borderColor) !== null && _a !== void 0 ? _a : theme.color.ui5; }};
`;
const ColorwayError = styled.p `
  margin: 0;
  max-width: 140px;
  ${({ theme }) => theme.font.t3};
  color: ${({ theme }) => { var _a; return (_a = theme.color.error) !== null && _a !== void 0 ? _a : '#b00020'; }};
`;
const ResetCameraButton = styled.div `
  width: 100px;
  cursor: pointer;

  button {
    width: 100%;
    color: ${({ theme }) => theme.color.ui3};
  }
`;
const ZoomButtonGroup = styled.div `
  display: flex;
  width: 100px;
`;
const ZoomButton = styled.button `
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  height: 32px;
  background-color: ${({ theme, $controlsStyle }) => { var _a; return (_a = $controlsStyle === null || $controlsStyle === void 0 ? void 0 : $controlsStyle.backgroundColor) !== null && _a !== void 0 ? _a : theme.color.ui6; }};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  border: 1px solid
    ${({ theme, $controlsStyle }) => { var _a; return (_a = $controlsStyle === null || $controlsStyle === void 0 ? void 0 : $controlsStyle.borderColor) !== null && _a !== void 0 ? _a : theme.color.ui5; }};
  box-shadow: ${({ theme }) => theme.shadows.outerShadow1};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  ${({ theme }) => theme.font.t3};
  color: ${({ theme, $controlsStyle }) => { var _a; return (_a = $controlsStyle === null || $controlsStyle === void 0 ? void 0 : $controlsStyle.textColor) !== null && _a !== void 0 ? _a : theme.color.ui3; }};
  box-sizing: border-box;

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }

  &:first-child {
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
    border-right: none;
  }

  &:last-child {
    border-top-right-radius: 20px;
    border-bottom-right-radius: 20px;
  }

  &:hover {
    background-color: ${({ theme, disabled, $controlsStyle }) => {
    if ($controlsStyle === null || $controlsStyle === void 0 ? void 0 : $controlsStyle.backgroundColor) {
        return $controlsStyle.backgroundColor;
    }
    const t = theme;
    return disabled ? t.color.ui6 : t.color.ui4;
}};
  }

  &:active {
    background-color: ${({ theme, disabled, $controlsStyle }) => {
    if ($controlsStyle === null || $controlsStyle === void 0 ? void 0 : $controlsStyle.backgroundColor) {
        return $controlsStyle.backgroundColor;
    }
    const t = theme;
    return disabled ? t.color.ui6 : t.color.ui3;
}};
  }
`;
const ZoomControls = styled(ZoomButtonGroup) `
  width: 100px;
`;
const ControlsContainer = styled.div `
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: absolute;

  ${({ $layout }) => {
    switch ($layout) {
        case 'tl':
            return 'top: 12px; left: 12px;';
        case 'tr':
            return 'top: 12px; right: 12px;';
        case 'bl':
            return 'bottom: 12px; left: 12px;';
        case 'br':
            return 'bottom: 12px; right: 12px;';
        default:
            return 'top: 12px; right: 12px;';
    }
}}
`;
const SaveExitPanel = styled.div `
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  justify-content: center;
  margin: 0 auto;
  max-width: 640px;
  padding: 24px;
  width: 100%;
`;
const SaveExitTitle = styled.h2 `
  margin: 0;
  ${({ theme }) => theme.font.h3};
  color: ${({ theme }) => theme.color.ui1};
`;
const SaveExitBody = styled.p `
  margin: 0;
  ${({ theme }) => theme.font.t3};
  color: ${({ theme }) => theme.color.ui2};
`;
const RecipeUrlCode = styled.pre `
  margin: 0;
  max-height: 160px;
  overflow: auto;
  padding: 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.ui6};
  border: 1px solid ${({ theme }) => theme.color.ui5};
  color: ${({ theme }) => theme.color.ui1};
  ${({ theme }) => theme.font.t3};
  white-space: pre-wrap;
  word-break: break-all;
`;
const RecipeUrlCopyStatus = styled.p `
  margin: 0;
  ${({ theme }) => theme.font.t3};
  color: ${({ theme }) => theme.color.ui2};
`;
const SaveExitErrorBanner = styled.div `
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.ui6};
  border: 1px solid ${({ theme }) => theme.color.ui5};
  ${({ theme }) => theme.font.t3};
  color: ${({ theme }) => { var _a; return (_a = theme.color.error) !== null && _a !== void 0 ? _a : '#b00020'; }};
  pointer-events: none;
`;
const SaveExitActions = styled.div `
  display: flex;
  gap: 8px;
`;
export const AppViewer = ({ controlsLayout = 'tr', controlsStyle, idleAnimationMode = 'static', onRecipeSaved, showGraphicsPanel = false, useBrandLogos = true, }) => {
    const { config, dispatch, featureFlags, handleUpdate, handleCameraReset, handleZoomIn, handleZoomOut, handleSaveAndExit, handleRecipeUrlCopy, hoverGraphicId, logoRegions, mockupLogos, recipeSource, recipeUrlCopyStatus, saveExitError, saveExitStatus, savedRecipeExpiresAt, savedRecipeUrl, scene, sceneIsLoading, selectedGraphicId, stylePreviewUrl, styleNotFoundMessage, viewerRef, visible, waitingForFirstFrame, } = useAppViewer(showGraphicsPanel, useBrandLogos);
    const { colorOptions, colorway, error: colorwayError, selectColorway, sessionToken, switching, } = useColorwayPicker();
    const resetButtonStyle = controlsStyle
        ? {
            color: controlsStyle.textColor,
            backgroundColor: controlsStyle.backgroundColor,
            borderColor: controlsStyle.borderColor,
        }
        : undefined;
    const recipeSaved = saveExitStatus === 'saved';
    const saveBusy = saveExitStatus === 'saving';
    useEffect(() => {
        if (recipeSaved) {
            onRecipeSaved === null || onRecipeSaved === void 0 ? void 0 : onRecipeSaved();
        }
    }, [onRecipeSaved, recipeSaved]);
    if (recipeSaved) {
        return (_jsx(Container, { children: _jsxs(SaveExitPanel, { children: [_jsx(SaveExitTitle, { children: "Recipe saved" }), _jsxs(SaveExitBody, { children: ["Your recipe URL expires ", formatRecipeExpiry(savedRecipeExpiresAt), ". Copy it now; this editor session is closed."] }), _jsx(RecipeUrlCode, { children: savedRecipeUrl }), _jsx(RecipeUrlCopyStatus, { children: recipeUrlCopyStatus === 'copied'
                            ? 'Copied to clipboard.'
                            : recipeUrlCopyStatus === 'error'
                                ? 'Copy failed. Please copy manually.'
                                : 'Ready to copy.' }), _jsx(SaveExitActions, { children: _jsx(Button, { text: "Copy URL", look: "positive", onClick: handleRecipeUrlCopy }) })] }) }));
    }
    return (_jsxs(Container, { children: [(sceneIsLoading || waitingForFirstFrame) && stylePreviewUrl && (_jsx(PreviewImage, { src: stylePreviewUrl, alt: "Style preview" })), (!scene || sceneIsLoading) && !styleNotFoundMessage && (_jsx(LoadingSpinner, { children: _jsx(Spinner, { type: "orbit" }) })), scene && (_jsxs(_Fragment, { children: [_jsx(Viewer, { backgroundAlpha: 0, backgroundColor: "#000000", cameraDefault: DEFAULT_CAMERA, config: config, dispatch: dispatch, featureFlags: featureFlags, hoverGraphicId: hoverGraphicId !== null && hoverGraphicId !== void 0 ? hoverGraphicId : null, id: "canvas", idleAnimationMode: idleAnimationMode, logoRegions: logoRegions, logos: mockupLogos, onUpdate: handleUpdate, preserveDrawingBuffer: true, ref: viewerRef, scene: scene, selectedGraphicId: selectedGraphicId !== null && selectedGraphicId !== void 0 ? selectedGraphicId : null, showGarmentGeometry: true, showGraphicsPanel: showGraphicsPanel, visible: visible }, showGraphicsPanel ? 'with-graphics' : 'no-graphics'), _jsxs(ControlsContainer, { "$layout": controlsLayout, children: [sessionToken && colorOptions.length > 1 && (_jsx(ColorwaySelect, { "aria-label": "Colorway", disabled: switching || sceneIsLoading, "$controlsStyle": controlsStyle, value: colorway !== null && colorway !== void 0 ? colorway : '', onChange: event => {
                                    void selectColorway(event.target.value);
                                }, children: colorOptions.map(color => (_jsx("option", { value: color, children: color }, color))) })), sessionToken && colorwayError && _jsx(ColorwayError, { children: colorwayError }), _jsx(ResetCameraButton, { onClick: handleCameraReset, children: _jsx(Button, { text: "Reset View", look: "negative", onClick: handleCameraReset, style: resetButtonStyle }) }), recipeSource != null && (_jsx(ResetCameraButton, { children: _jsx(Button, { text: saveBusy ? 'Saving…' : 'Save & Exit', look: "negative", onClick: handleSaveAndExit, disabled: saveBusy, style: resetButtonStyle }) })), _jsxs(ZoomControls, { children: [_jsx(ZoomButton, { onClick: handleZoomOut, "$controlsStyle": controlsStyle, children: _jsx(DecreaseIcon, {}) }), _jsx(ZoomButton, { onClick: handleZoomIn, "$controlsStyle": controlsStyle, children: _jsx(IncreaseIcon, {}) })] })] }), saveExitError ? _jsx(SaveExitErrorBanner, { children: saveExitError }) : null] })), !scene && styleNotFoundMessage && _jsx(MessageOverlay, { children: styleNotFoundMessage })] }));
};
